import {ComponentRef, Injectable, ViewContainerRef} from '@angular/core';
import {PlanService, Point, TileViewerReadyEvent} from '@mydocma/tile-viewer';
import {DivIcon, DragEndEvent, LatLng, LayerGroup, LeafletMouseEvent, Map, Marker} from 'leaflet';
import {copy} from '@mydocma/common/src/lib/deep';
import {locationPin} from '../../configs/location-pin';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {Pin} from '@mydocma/tile-viewer/lib/pin/pin';
import {ClickSyncMarkerComponent} from '../plan-viewer/common/click-sync-marker/click-sync-marker.component';


@Injectable()
export class PlanLocationService {
  private markerLocationChangeSubject = new Subject<Point>();
  public onMarkerLocationChange = this.markerLocationChangeSubject.asObservable();
  private planReadyData: TileViewerReadyEvent;
  private pinMovementSubscription: Subscription;
  private mapClickEventListener: (event: LeafletMouseEvent) => void;
  private map: Map;
  private dragEndListener: () => void;
  private dragStartListener: () => void;


  private locationLayerGroup: LayerGroup;

  private locationMarkerVisibilitySubject = new BehaviorSubject<boolean>(true);

  // remvoe pin service
  private currentLocationMarker: Marker;
  private locationMarkerComponentRef: ComponentRef<ClickSyncMarkerComponent>;

  constructor(private viewContainerRef: ViewContainerRef) {
    this.mapClickEventListener = this.handleMapClick.bind(this);
    this.dragEndListener = this.handleDragEndMarker.bind(this);
    this.dragStartListener = this.handleMarkerDragStart.bind(this);
  }

  private _currentLocationPin: Pin;

  get currentLocationPin() {
    return this._currentLocationPin;
  }

  // Attaches plan data to the service
  public attachPlanData(planData: TileViewerReadyEvent) {
    this.planReadyData = planData;
    this.map = this.planReadyData.mapService.map;
    this.initializeLocationPinLayer();
  }

  // Sets the location of the pin
  public setLocationMarker(point: Point): void {

    const planService = this.planReadyData.mapService as PlanService;
    const transformedCoords = planService.map.unproject([point.x, point.y], planService.plan.maxZoom);
    if (this.currentLocationMarker) {
      this.updateLocationMarkerPosition(transformedCoords);
      return;
    }
    const pin = copy(locationPin);
    pin.anchor = {x: transformedCoords.lat, y: transformedCoords.lng};
    this._currentLocationPin = pin;
    this.currentLocationMarker = new Marker(transformedCoords, {icon: this.getLocationDivIcon(pin), draggable: true});
    this.currentLocationMarker.on('dragend', this.dragEndListener);
    this.currentLocationMarker.on('dragstart', this.dragStartListener)
    this.currentLocationMarker.addTo(this.locationLayerGroup);
    this.unsubscribeFromPreviousPinMovements();
    this.subscribeToPinMovements();
    this.planReadyData.mapService.map.on('click', this.mapClickEventListener);
    // Todo: Find a way to add pins in plan without using setTimeout
  }

  public setLocationMarkerAngle(angle: number) {
    if (this.locationMarkerComponentRef) {
      this.locationMarkerComponentRef.instance.updateMarkerRotation(angle);
    }

  }

  public disableLocationEvents() {
    this.unsubscribeFromPreviousPinMovements();
  }

  //
  public enableLocationEvents() {
    this.unsubscribeFromPreviousPinMovements();
    this.subscribeToPinMovements();

    this.planReadyData.mapService.map.on('click', this.mapClickEventListener);
  }

  public hideLocationMarker() {
    if (this.locationMarkerVisibilitySubject.value) {
      this.locationLayerGroup.removeFrom(this.map);
      this.locationMarkerVisibilitySubject.next(false);
    }

  }

  public showLocationMarker() {
    if (!this.locationMarkerVisibilitySubject.value) {
      this.locationLayerGroup.addTo(this.map);
      this.locationMarkerVisibilitySubject.next(true);
    }
  }

  private handleMarkerDragStart() {
    this.locationMarkerComponentRef.instance.draggable = true;

  }

  private handleDragEndMarker(event: DragEndEvent): void {
    const {transformedProjectPoint, transformedRelativePoint} = this.transformCoordinates(event.target._latlng);
    this.locationMarkerComponentRef.instance.draggable = false;
    this.markerLocationChangeSubject.next(transformedProjectPoint);
  }

  // Unsubscribes from previous pin movements and map click events
  private unsubscribeFromPreviousPinMovements() {
    if (this.pinMovementSubscription) {
      this.pinMovementSubscription?.unsubscribe();
    }
    this.planReadyData.mapService.map.off('click', this.mapClickEventListener);
  }

  // Subscribes to pin movements
  private subscribeToPinMovements() {
    this.pinMovementSubscription = this.planReadyData.pinEditService.onPinMoved$.subscribe(marker => {
      this.updateLocationMarkerPosition(marker.getLatLng(), false);
    });
  }

  // Handles map click events
  private handleMapClick(event: LeafletMouseEvent) {
    this.updateLocationMarkerPosition(event.latlng);
  }

  // Updates the location of the pin
  private updateLocationMarkerPosition(latLng: LatLng, shouldUpdatePinPosition = true) {
    const {transformedProjectPoint, transformedRelativePoint} = this.transformCoordinates(latLng);
    if (shouldUpdatePinPosition && this.currentLocationMarker) {
      this.currentLocationMarker.setLatLng(latLng);
    }
    this.markerLocationChangeSubject.next(transformedProjectPoint);
  }

  // Transforms coordinates for the pin location
  private transformCoordinates(latlng: LatLng): { transformedRelativePoint: Point, transformedProjectPoint: any } {
    const planService = this.planReadyData.mapService as PlanService;
    const transformedProjectPoint = planService.map.project(latlng, planService.plan.maxZoom);
    const transformedRelativePoint = planService.geoMapToPinSystemCoordinates(latlng);
    return {transformedProjectPoint, transformedRelativePoint};
  }

  private initializeLocationPinLayer() {
    if (this.locationLayerGroup) {
      this.currentLocationMarker = null;
      this.locationMarkerComponentRef.destroy();
      this._currentLocationPin = null;
      this.locationLayerGroup.removeFrom(this.map);
    }
    this.locationLayerGroup = new LayerGroup();
    this.locationLayerGroup.addTo(this.map);
  }

  private getLocationDivIcon(pin: Pin): DivIcon {
    const containerDiv = document.createElement('div');
    this.locationMarkerComponentRef = this.viewContainerRef.createComponent(ClickSyncMarkerComponent);
    this.locationMarkerComponentRef.instance.attachToPin(pin);
    // Make sure the component is initialized before accessing its native element
    const nativeElement = this.locationMarkerComponentRef.location.nativeElement;
    containerDiv.appendChild(nativeElement);
    return new DivIcon({
      html: containerDiv,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [1, -34],
      className: 'arrow-icon',
    });
  }
}
